import React, { useEffect, useState } from 'react';
import { bonushuntApi } from '../services/api';
import { Bonushunt, BonushuntEntry } from '../types';
import './Bonushunts.css';

const Bonushunts: React.FC = () => {
  const [hunts, setHunts] = useState<Bonushunt[]>([]);
  const [selectedHunt, setSelectedHunt] = useState<Bonushunt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewHuntForm, setShowNewHuntForm] = useState(false);
  const [showAddSlotForm, setShowAddSlotForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<BonushuntEntry | null>(null);

  // New Hunt Form
  const [newHuntData, setNewHuntData] = useState({
    name: '',
    hunt_date: new Date().toISOString().split('T')[0],
    start_balance: ''
  });

  // Add Slot Form
  const [newSlotData, setNewSlotData] = useState({
    slot_name: '',
    bet_amount: ''
  });

  useEffect(() => {
    fetchHunts();
  }, []);

  const fetchHunts = async () => {
    try {
      const response = await bonushuntApi.getAll();
      setHunts(response.data);
    } catch (error) {
      console.error('Error fetching hunts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHuntDetails = async (id: number) => {
    try {
      const response = await bonushuntApi.getOne(id);
      setSelectedHunt(response.data);
    } catch (error) {
      console.error('Error fetching hunt:', error);
    }
  };

  const handleCreateHunt = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await bonushuntApi.create({
        name: newHuntData.name || undefined,
        hunt_date: newHuntData.hunt_date,
        start_balance: newHuntData.start_balance ? parseFloat(newHuntData.start_balance) : 0
      });
      setHunts([response.data, ...hunts]);
      setSelectedHunt(response.data);
      setShowNewHuntForm(false);
      setNewHuntData({ name: '', hunt_date: new Date().toISOString().split('T')[0], start_balance: '' });
    } catch (error) {
      console.error('Error creating hunt:', error);
    }
  };

  const handleDeleteHunt = async (id: number) => {
    if (!window.confirm('Bonushunt wirklich löschen?')) return;
    try {
      await bonushuntApi.delete(id);
      setHunts(hunts.filter(h => h.id !== id));
      if (selectedHunt?.id === id) {
        setSelectedHunt(null);
      }
    } catch (error) {
      console.error('Error deleting hunt:', error);
    }
  };

  const handleCompleteHunt = async () => {
    if (!selectedHunt) return;
    try {
      await bonushuntApi.update(selectedHunt.id, {
        name: selectedHunt.name || undefined,
        hunt_date: selectedHunt.hunt_date.split('T')[0],
        start_balance: selectedHunt.start_balance,
        status: 'completed'
      });
      fetchHunts();
      fetchHuntDetails(selectedHunt.id);
    } catch (error) {
      console.error('Error completing hunt:', error);
    }
  };

  const handleAddSlot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedHunt) return;
    try {
      const response = await bonushuntApi.addEntry(selectedHunt.id, {
        slot_name: newSlotData.slot_name,
        bet_amount: parseFloat(newSlotData.bet_amount)
      });
      setSelectedHunt({
        ...selectedHunt,
        entries: [...(selectedHunt.entries || []), response.data]
      });
      setNewSlotData({ slot_name: '', bet_amount: '' });
      setShowAddSlotForm(false);
      fetchHunts();
    } catch (error) {
      console.error('Error adding slot:', error);
    }
  };

  const handleUpdateEntry = async (entry: BonushuntEntry, winAmount: number | null, opened: boolean) => {
    if (!selectedHunt) return;
    try {
      const response = await bonushuntApi.updateEntry(selectedHunt.id, entry.id, {
        slot_name: entry.slot_name,
        bet_amount: entry.bet_amount,
        win_amount: winAmount ?? undefined,
        opened
      });
      setSelectedHunt({
        ...selectedHunt,
        entries: selectedHunt.entries?.map(e => e.id === entry.id ? response.data : e)
      });
      setEditingEntry(null);
      fetchHunts();
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleDeleteEntry = async (entryId: number) => {
    if (!selectedHunt) return;
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await bonushuntApi.deleteEntry(selectedHunt.id, entryId);
      setSelectedHunt({
        ...selectedHunt,
        entries: selectedHunt.entries?.filter(e => e.id !== entryId)
      });
      fetchHunts();
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const calculateStats = (hunt: Bonushunt) => {
    const entries = hunt.entries || [];
    const totalFS = entries.length;
    const openedEntries = entries.filter(e => e.opened);
    const unopenedEntries = entries.filter(e => !e.opened);
    const leftFS = unopenedEntries.length;
    const totalCost = entries.reduce((sum, e) => sum + e.bet_amount, 0);
    const totalWon = openedEntries.reduce((sum, e) => sum + (e.win_amount || 0), 0);
    const profit = totalWon - hunt.start_balance;
    const avgBet = totalFS > 0 ? totalCost / totalFS : 0;
    const avgWin = openedEntries.length > 0 ? totalWon / openedEntries.length : 0;
    const avgMulti = openedEntries.length > 0
      ? openedEntries.reduce((sum, e) => sum + (e.multiplier || 0), 0) / openedEntries.length
      : 0;
    const breakeven = avgBet > 0 ? hunt.start_balance / avgBet : 0;
    const rtp = hunt.start_balance > 0 ? (totalWon / hunt.start_balance) * 100 : 0;

    // Berechne benötigten X-Faktor für Breakeven
    // Formel: (Start - bisheriger Gewinn) / Summe der verbleibenden Einsätze
    const remainingBetSum = unopenedEntries.reduce((sum, e) => sum + e.bet_amount, 0);
    const neededForBreakeven = hunt.start_balance - totalWon;
    const requiredMultiplier = remainingBetSum > 0 ? neededForBreakeven / remainingBetSum : 0;

    return {
      totalFS, leftFS, totalCost, totalWon, profit, avgBet, avgWin, avgMulti, breakeven, rtp,
      remainingBetSum, neededForBreakeven, requiredMultiplier
    };
  };

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="bonushunts-page">
      <div className="bonushunts-header">
        <h1>Bonushunts</h1>
        <button className="btn-new" onClick={() => setShowNewHuntForm(true)}>
          + Neuer Hunt
        </button>
      </div>

      {showNewHuntForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Neuer Bonushunt</h2>
            <form onSubmit={handleCreateHunt}>
              <div className="form-group">
                <label>Name (optional)</label>
                <input
                  type="text"
                  value={newHuntData.name}
                  onChange={(e) => setNewHuntData({ ...newHuntData, name: e.target.value })}
                  placeholder="z.B. Sunday Hunt"
                />
              </div>
              <div className="form-group">
                <label>Datum</label>
                <input
                  type="date"
                  value={newHuntData.hunt_date}
                  onChange={(e) => setNewHuntData({ ...newHuntData, hunt_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Startguthaben (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newHuntData.start_balance}
                  onChange={(e) => setNewHuntData({ ...newHuntData, start_balance: e.target.value })}
                  placeholder="500.00"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowNewHuntForm(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-save">Erstellen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bonushunts-layout">
        <div className="hunts-list">
          <h3>Meine Hunts</h3>
          {hunts.length === 0 ? (
            <p className="no-data">Noch keine Hunts vorhanden</p>
          ) : (
            hunts.map(hunt => (
              <div
                key={hunt.id}
                className={`hunt-card ${selectedHunt?.id === hunt.id ? 'active' : ''} ${hunt.status}`}
                onClick={() => fetchHuntDetails(hunt.id)}
              >
                <div className="hunt-card-header">
                  <span className="hunt-name">{hunt.name || 'Bonushunt'}</span>
                  <span className={`hunt-status ${hunt.status}`}>
                    {hunt.status === 'active' ? 'Aktiv' : 'Abgeschlossen'}
                  </span>
                </div>
                <div className="hunt-card-date">
                  {new Date(hunt.hunt_date).toLocaleDateString('de-DE')}
                </div>
                <div className="hunt-card-stats">
                  <span>{hunt.total_entries || 0} Slots</span>
                  <span>{formatCurrency(hunt.start_balance)}</span>
                </div>
                <button
                  className="btn-delete-small"
                  onClick={(e) => { e.stopPropagation(); handleDeleteHunt(hunt.id); }}
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>

        <div className="hunt-details">
          {selectedHunt ? (
            <>
              <div className="hunt-details-header">
                <div>
                  <h2>{selectedHunt.name || 'Bonushunt'}</h2>
                  <span className="hunt-date">
                    {new Date(selectedHunt.hunt_date).toLocaleDateString('de-DE')}
                  </span>
                </div>
                <div className="hunt-actions">
                  {selectedHunt.status === 'active' && (
                    <>
                      <button className="btn-add" onClick={() => setShowAddSlotForm(true)}>
                        + Slot hinzufügen
                      </button>
                      <button className="btn-complete" onClick={handleCompleteHunt}>
                        Hunt abschließen
                      </button>
                    </>
                  )}
                </div>
              </div>

              {(() => {
                const stats = calculateStats(selectedHunt);
                return (
                  <>
                    {/* Dynamische Breakeven-Anzeige */}
                    {stats.leftFS > 0 && selectedHunt.status === 'active' && (
                      <div className={`breakeven-indicator ${stats.requiredMultiplier <= 0 ? 'in-profit' : stats.requiredMultiplier > 100 ? 'critical' : stats.requiredMultiplier > 50 ? 'warning' : 'normal'}`}>
                        <div className="breakeven-main">
                          {stats.requiredMultiplier <= 0 ? (
                            <>
                              <span className="breakeven-title">Bereits im Profit!</span>
                              <span className="breakeven-value profit">{formatCurrency(Math.abs(stats.neededForBreakeven))}</span>
                            </>
                          ) : (
                            <>
                              <span className="breakeven-title">Benötigter Ø X-Faktor für Breakeven</span>
                              <span className="breakeven-value">{stats.requiredMultiplier.toFixed(2)}x</span>
                            </>
                          )}
                        </div>
                        <div className="breakeven-details">
                          <span>Verbleibend: {stats.leftFS} FS</span>
                          <span>Einsätze: {formatCurrency(stats.remainingBetSum)}</span>
                          {stats.requiredMultiplier > 0 && (
                            <span>Benötigt: {formatCurrency(stats.neededForBreakeven)}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {stats.leftFS === 0 && (
                      <div className={`breakeven-indicator ${stats.profit >= 0 ? 'in-profit' : 'critical'}`}>
                        <div className="breakeven-main">
                          <span className="breakeven-title">Hunt abgeschlossen</span>
                          <span className={`breakeven-value ${stats.profit >= 0 ? 'profit' : 'loss'}`}>
                            {stats.profit >= 0 ? '+' : ''}{formatCurrency(stats.profit)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="hunt-stats-grid">
                      <div className="stat-box">
                        <span className="stat-label">Total FS</span>
                        <span className="stat-value">{stats.totalFS}</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">Left FS</span>
                        <span className="stat-value">{stats.leftFS}</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">Hunt Start</span>
                        <span className="stat-value">{formatCurrency(selectedHunt.start_balance)}</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">Hunt Ende</span>
                        <span className="stat-value">{formatCurrency(stats.totalWon)}</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">Profit</span>
                        <span className={`stat-value ${stats.profit >= 0 ? 'positive' : 'negative'}`}>
                          {formatCurrency(stats.profit)}
                        </span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">BreakEven</span>
                        <span className="stat-value">{stats.breakeven.toFixed(2)}</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">RTP</span>
                        <span className="stat-value">{stats.rtp.toFixed(2)}%</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">Ø Einsatz</span>
                        <span className="stat-value">{formatCurrency(stats.avgBet)}</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">Ø Gewinn</span>
                        <span className="stat-value">{formatCurrency(stats.avgWin)}</span>
                      </div>
                      <div className="stat-box">
                        <span className="stat-label">Ø Multi</span>
                        <span className="stat-value">{stats.avgMulti.toFixed(2)}x</span>
                      </div>
                    </div>
                  </>
                );
              })()}

              <div className="entries-table">
                <table>
                  <thead>
                    <tr>
                      <th>Slot</th>
                      <th>Einsatz</th>
                      <th>Gewinn</th>
                      <th>X-Faktor</th>
                      <th>Status</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedHunt.entries || []).map(entry => (
                      <tr key={entry.id} className={entry.opened ? 'opened' : ''}>
                        <td className="slot-name">{entry.slot_name}</td>
                        <td>{formatCurrency(entry.bet_amount)}</td>
                        <td className={entry.win_amount !== null ? (entry.win_amount > entry.bet_amount ? 'positive' : 'negative') : ''}>
                          {entry.win_amount !== null ? formatCurrency(entry.win_amount) : '-'}
                        </td>
                        <td>{entry.multiplier !== null ? `${entry.multiplier.toFixed(1)}x` : '-'}</td>
                        <td>
                          <span className={`status-badge ${entry.opened ? 'opened' : 'pending'}`}>
                            {entry.opened ? 'Geöffnet' : 'Ausstehend'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          {!entry.opened && selectedHunt.status === 'active' && (
                            <button
                              className="btn-open"
                              onClick={() => setEditingEntry(entry)}
                            >
                              Öffnen
                            </button>
                          )}
                          {selectedHunt.status === 'active' && (
                            <button
                              className="btn-delete-entry"
                              onClick={() => handleDeleteEntry(entry.id)}
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!selectedHunt.entries || selectedHunt.entries.length === 0) && (
                  <p className="no-data">Noch keine Slots hinzugefügt</p>
                )}
              </div>
            </>
          ) : (
            <div className="no-hunt-selected">
              <p>Wähle einen Hunt aus oder erstelle einen neuen</p>
            </div>
          )}
        </div>
      </div>

      {showAddSlotForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Slot hinzufügen</h2>
            <form onSubmit={handleAddSlot}>
              <div className="form-group">
                <label>Slot Name</label>
                <input
                  type="text"
                  value={newSlotData.slot_name}
                  onChange={(e) => setNewSlotData({ ...newSlotData, slot_name: e.target.value })}
                  placeholder="z.B. Angel vs Sinner"
                  required
                />
              </div>
              <div className="form-group">
                <label>Einsatz (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={newSlotData.bet_amount}
                  onChange={(e) => setNewSlotData({ ...newSlotData, bet_amount: e.target.value })}
                  placeholder="0.60"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowAddSlotForm(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-save">Hinzufügen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingEntry && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Freispiel öffnen</h2>
            <p className="modal-slot-name">{editingEntry.slot_name}</p>
            <p className="modal-bet">Einsatz: {formatCurrency(editingEntry.bet_amount)}</p>
            <form onSubmit={(e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const winInput = form.elements.namedItem('win_amount') as HTMLInputElement;
              handleUpdateEntry(editingEntry, parseFloat(winInput.value), true);
            }}>
              <div className="form-group">
                <label>Gewinn (€)</label>
                <input
                  type="number"
                  name="win_amount"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  required
                  autoFocus
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setEditingEntry(null)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-save">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bonushunts;
